import isEqual from '@wry/equality';

import {assert, assertAndContinue} from 'Asserts';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {
  DEFAULT_ENTITY_SCHEMA_OPTION_NAME,
  getAnySchemaOptionName,
} from 'notebook/structured-tables/lookup-columns/NotebookLookupColumnUtils';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {GridSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {getTranslatedTagSchemaFromCache} from 'schemas/util/getTagSchemaFromCache';
import ErrorSubclass from 'util/ErrorSubclass';
import * as logging from 'util/Logging';
import {MakeFieldsOptional} from 'util/Types';

import {ProcessTaskSchemaGraphQL} from '../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {
  getAllBoxSchemasFromCache,
  getAllPlateSchemasFromCache,
  getAllProcessTaskSchemasFromCache,
  getContainerSchemaFromCache,
  getStorableSchemaFromCache,
  getTagSchemaOrSchemaInterfaceFromCache,
} from './AllSchemasQueries';
import {LookupConfigModalPane, LookupConfigModalProps} from './LookupConfigModal';
import {
  ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES,
  CollapsingLookupStepInfo,
  ContainerByGridPositionLookupStepSpec,
  DELETED_SCHEMA_ID,
  DestinationLookupStepSpec,
  FillDirection,
  FilterLookupStepInfo,
  FilterType,
  InjectedLookupStep,
  LookupConfigModalContextInfo,
  LookupStepInfo,
  LookupStepSpec,
  LookupStepType,
  MODIFIER_LOOKUP_STEP_TYPES,
  ModifierLookupStepInfo,
  NotebookColumnItemType,
  NotebookColumnTypecheckLookupStepSpec,
  ReplicatesLookupStepSpec,
  RIGHT_ARROW,
  SourceLookupStepSpec,
  STORED_WITHIN_FILTER_STEP_NAME,
  StoredWithinFilterLookupStepInfo,
  TaskFinderProcessTasksLookupStepSpec,
} from './LookupConfigModalConstants';
import {
  filterOptionsForFieldType,
  getItemStringForLookupStepType,
  getLinkSchemaForLookupStep,
  getLookupStepInfoWithType,
  getPlateOrBoxContainerLabel,
  getRequiredLinkSchema,
  getSchemaFieldsForLookupStep,
  getStepDisplayInfo,
  getStepItemWithSourceDestinationType,
  hasLookupStepContext,
  isFieldStep,
  isStepWithTypecastSchemaMetadata,
} from './LookupConfigUtils';
import {isDetailStep} from './SchemaFieldSelectTreeUtils';

export function getLookupStepInfoForCountStepItem(
  parentSchema: Schema | null | undefined,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean,
  isFirstStep?: boolean
): LookupStepInfo {
  const schemaField = parentSchema
    ? getSchemaFieldsForLookupStep(parentSchema, Boolean(isFirstStep), schemaFieldFilterFn)[0]
    : null;
  return {
    name: `Count field values`,
    lookupStep: {type: LookupStepType.COUNT, schemaField: schemaField ? schemaField.apiId : null},
    parentSchema: parentSchema ?? null,
    linkSchema: null,
  };
}

export function getLookupStepInfoForProcessTaskStepItem(
  processTaskSchema: Schema | null | undefined,
  parentSchema: Schema | null | undefined
): LookupStepInfo {
  assert(processTaskSchema != null, 'Expected process task schema to be defined for process task step');
  return {
    name: 'Task',
    lookupStep: {
      type: LookupStepType.TASK_FINDER_PROCESS_TASKS,
      linkedBySchemaField: processTaskSchema.fields[0]?.apiId ?? null,
    },
    parentSchema: parentSchema ?? null,
    linkSchema: processTaskSchema,
  };
}

export function getLookupStepInfoForFlowchartProcessTaskStepItem(
  flowchartTaskSchema: Schema,
  parentSchema: Schema | null | undefined
): LookupStepInfo {
  return {
    name: 'Flowchart task',
    lookupStep: {
      type: LookupStepType.FLOWCHART_PROCESS_TASK,
      processTaskSchema: flowchartTaskSchema.id,
    },
    parentSchema: parentSchema ?? null,
    linkSchema: flowchartTaskSchema,
  };
}

export function getLookupStepInfoForSchemaIdentityStepItem(
  entitySchema: Schema | null,
  parentSchema: Schema | null | undefined
): LookupStepInfo {
  assert(entitySchema != null, 'Expected entity schema to be defined for schema identity step');
  return {
    name: entitySchema.name,
    lookupStep: {
      type: LookupStepType.SCHEMA_IDENTITY,
      entitySchema: entitySchema.id,
    },
    parentSchema: parentSchema ?? null,
    linkSchema: entitySchema,
  };
}

export function getLookupStepInfoForContainerByGridPositionStepItem(
  lookupStep: ContainerByGridPositionLookupStepSpec,
  parentSchema: Schema | null | undefined
): LookupStepInfo {
  const gridField = parentSchema
    ? parentSchema.allFields.find(
        (field) => field.apiId === lookupStep.schemaField || field.systemName === lookupStep.schemaField
      )
    : null;
  let linkSchema = null;
  let gridSchema = null;
  if (gridField != null) {
    gridSchema = getRequiredLinkSchema(gridField) as GridSchema | null;
    linkSchema = gridSchema?.containerSchemaId
      ? getContainerSchemaFromCache(gridSchema.containerSchemaId)
      : null;
  }
  const gridPositionField = parentSchema
    ? parentSchema.allFields.find((field) => field.apiId === lookupStep.gridPositionSchemaField)
    : null;
  const defaultLabel = 'Well or container';
  const name = gridField ? getPlateOrBoxContainerLabel(gridField, defaultLabel, gridSchema) : defaultLabel;
  return {
    name: `${name} in ${gridField ? gridField.displayName : 'plate or box'}`,
    lookupStep: {
      type: LookupStepType.CONTAINER_BY_GRID_POSITION,
      schemaField: gridField ? gridField.apiId : null,
      gridPositionSchemaField: gridPositionField ? gridPositionField.apiId : null,
    },
    schemaField: gridField,
    parentSchema: parentSchema ?? null,
    linkSchema,
  };
}

/**
 * Given a notebook column typecheck lookup step, generate a lookup step info that can represent initial
 * lookups against columns with or without specified schemas, for both entity and storable column types.
 */
export function getLookupStepInfoForNotebookTypeCheckItem(
  lookupStep: NotebookColumnTypecheckLookupStepSpec,
  parentSchema: Schema | null | undefined,
  {storableLookupMustBeParent}: {storableLookupMustBeParent?: boolean} = {}
): LookupStepInfo {
  let name = DEFAULT_ENTITY_SCHEMA_OPTION_NAME;
  let linkSchema = null;
  if (lookupStep.notebookColumnItemType === NotebookColumnItemType.ENTITY) {
    if (lookupStep.notebookColumnEntitySchema == null) {
      name = DEFAULT_ENTITY_SCHEMA_OPTION_NAME;
    } else {
      linkSchema = getTagSchemaOrSchemaInterfaceFromCache(lookupStep.notebookColumnEntitySchema);
      const schemaName = linkSchema?.name;
      assertAndContinue(
        schemaName != null,
        `LookupStep specified nonexistent schema: ${lookupStep.notebookColumnEntitySchema}`
      );
      name = schemaName ?? DEFAULT_ENTITY_SCHEMA_OPTION_NAME;
    }
  } else if (ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES.includes(lookupStep.notebookColumnItemType)) {
    if (lookupStep.notebookColumnStorableSchema == null) {
      name = getAnySchemaOptionName(lookupStep.notebookColumnItemType);
    } else {
      linkSchema = getStorableSchemaFromCache(lookupStep.notebookColumnStorableSchema) ?? null;
      const schemaName = linkSchema?.name;
      assertAndContinue(
        schemaName != null,
        `LookupStep specified nonexistent schema: ${lookupStep.notebookColumnStorableSchema}`
      );
      name = schemaName ?? getAnySchemaOptionName(lookupStep.notebookColumnItemType);
    }
  }

  return {
    name,
    lookupStep,
    parentSchema: parentSchema ?? null,
    linkSchema,
    storableLookupMustBeParent,
  };
}

export function getLookupStepInfoForNonFieldStepItem(
  stepType: LookupStepType,
  parentSchema: Schema | null,
  previousStepType?: LookupStepType
): LookupStepInfo {
  switch (stepType) {
    case LookupStepType.ALIASES:
      return {
        name: 'Aliases',
        lookupStep: {type: LookupStepType.ALIASES},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.NAME:
      return {
        name: 'Name',
        lookupStep: {type: LookupStepType.NAME},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.SAMPLE_OWNERS:
      return {
        name: 'Sample owners',
        lookupStep: {type: LookupStepType.SAMPLE_OWNERS},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.AUTHORIZED_USERS:
      return {
        name: getRestrictedSampleUsersHeaderText(),
        lookupStep: {type: LookupStepType.AUTHORIZED_USERS},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.AVAILABLE_SPACE:
      return {
        name: 'Available space',
        lookupStep: {type: LookupStepType.AVAILABLE_SPACE},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.BARCODE:
      return {
        name: 'Barcode',
        lookupStep: {type: LookupStepType.BARCODE},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.BOX:
      return {
        name: 'Box',
        lookupStep: {type: LookupStepType.BOX},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.CHEMICAL_STRUCTURE:
      return {
        name: 'Chemical structure',
        lookupStep: {
          type: LookupStepType.CHEMICAL_STRUCTURE,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.CHILD_ENTITIES:
      return {
        name: 'Child Entities',
        lookupStep: {type: LookupStepType.CHILD_ENTITIES},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.COMPUTED_EXPIRATION_DATE:
      return {
        name: 'Expiration date',
        lookupStep: {type: LookupStepType.COMPUTED_EXPIRATION_DATE},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.FREEZE_THAW_COUNT:
      return {
        name: 'Freeze/thaw count',
        lookupStep: {type: LookupStepType.FREEZE_THAW_COUNT},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.CONCENTRATION:
      return {
        name: 'Concentration',
        lookupStep: {type: LookupStepType.CONCENTRATION, concentrationUnits: 'uM'},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.CONTAINER:
      return {
        name: 'Container',
        lookupStep: {type: LookupStepType.CONTAINER},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.CONTENTS:
      return {
        name: 'Contents',
        lookupStep: {type: LookupStepType.CONTENTS},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.PLACEHOLDER_BOXES: {
      const allBoxSchemas = getAllBoxSchemasFromCache();
      const boxSchema = allBoxSchemas.length ? allBoxSchemas[0] : null;
      return {
        name: 'Placeholder Boxes',
        lookupStep: {
          type: LookupStepType.PLACEHOLDER_BOXES,
          boxSchema: boxSchema ? boxSchema.id : DELETED_SCHEMA_ID,
        },
        parentSchema,
        linkSchema: boxSchema,
      };
    }
    case LookupStepType.LATEST: {
      const itemString = getItemStringForLookupStepType(previousStepType);
      return {
        name: `Latest ${itemString}`,
        lookupStep: {type: LookupStepType.LATEST},
        parentSchema,
        linkSchema: null,
      };
    }
    case LookupStepType.LOCATION:
      return {
        name: 'Location',
        lookupStep: {type: LookupStepType.LOCATION},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.LOCATION_PATH:
      return {
        name: 'Location path',
        lookupStep: {type: LookupStepType.LOCATION_PATH},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.PLACEHOLDER_PLATES: {
      const allPlateSchemas = getAllPlateSchemasFromCache();
      const plateSchema = allPlateSchemas.length ? allPlateSchemas[0] : null;
      return {
        name: 'Placeholder Plates',
        lookupStep: {
          type: LookupStepType.PLACEHOLDER_PLATES,
          plateSchema: plateSchema ? plateSchema.id : DELETED_SCHEMA_ID,
        },
        parentSchema,
        linkSchema: plateSchema,
      };
    }
    case LookupStepType.PLATE:
      return {
        name: 'Plate',
        lookupStep: {type: LookupStepType.PLATE},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.REGISTRY_IDENTIFIER:
      return {
        name: 'Registry ID',
        lookupStep: {type: LookupStepType.REGISTRY_IDENTIFIER},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.REPLICATES:
      return {
        name: 'Replicates',
        lookupStep: {
          type: LookupStepType.REPLICATES,
          numberLookupConfig: {isMulti: false, lookupSteps: []},
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.RESIDUES:
      return {
        name: 'Residues',
        lookupStep: {
          type: LookupStepType.RESIDUES,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.RESULTS:
      return {
        name: 'Results',
        lookupStep: {
          type: LookupStepType.RESULTS,
          linkedBySchemaField: null,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.QUANTITY:
      return {
        name: getQuantityDisplayString(),
        lookupStep: {
          type: LookupStepType.QUANTITY,
          volumeUnits: 'uL',
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.WELL_COORDINATES:
      return {
        name: 'Position',
        lookupStep: {
          type: LookupStepType.WELL_COORDINATES,
          coordinatesFillDirection: null,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.BOX_CONTAINERS:
    case LookupStepType.WELLS: {
      const containerSchemaId = (parentSchema as GridSchema | null)?.containerSchemaId;
      return {
        name: stepType === LookupStepType.BOX_CONTAINERS ? 'Box Containers' : 'Wells',
        parentSchema,
        linkSchema: containerSchemaId ? getContainerSchemaFromCache(containerSchemaId) : null,
        lookupStep: {
          type: stepType,
          filter: {
            ignoreEmpty: false,
            ignoreFilled: false,
            rowsToIgnore: [],
            columnsToIgnore: [],
            wellsToIgnore: [],
          },
          order: {
            fillDirection: FillDirection.ACROSS_ROWS,
            skipRows: 0,
            skipColumns: 0,
            fillByQuadrant: false,
          },
        },
      };
    }
    case LookupStepType.CONSTANT:
      return {
        name: 'Constant',
        lookupStep: {
          type: LookupStepType.CONSTANT,
          value: '',
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.PROCESS_TASK: {
      const taskSchema = parentSchema
        ? getAllProcessTaskSchemasFromCache().find(
            (schema: Schema) => (schema as ProcessTaskSchemaGraphQL).outputSchema?.id === parentSchema.id
          )
        : null;
      return {
        name: 'Workflow task',
        parentSchema,
        linkSchema: taskSchema ?? null,
        lookupStep: {
          type: LookupStepType.PROCESS_TASK,
        },
      };
    }
    case LookupStepType.TASK_FINDER_PROCESS_TASKS:
      return {
        name: 'Task',
        lookupStep: {
          type: LookupStepType.TASK_FINDER_PROCESS_TASKS,
          linkedBySchemaField: null,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.FILTER_BY_ORIGIN:
      return {
        name: 'Filter By Origin',
        lookupStep: {
          type: LookupStepType.FILTER_BY_ORIGIN,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.STUDIES:
      return {
        name: 'Studies',
        lookupStep: {type: LookupStepType.STUDIES},
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.TRANSLATION_LINKS_TO_ENTITY:
      return {
        name: 'Translation links to entity',
        lookupStep: {
          type: LookupStepType.TRANSLATION_LINKS_TO_ENTITY,
          entitySchema: undefined,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.PART_LINKS_TO_ENTITY:
      return {
        name: 'Part links to sequence',
        lookupStep: {
          type: LookupStepType.PART_LINKS_TO_ENTITY,
          entitySchema: undefined,
        },
        parentSchema,
        linkSchema: null,
      };
    case LookupStepType.AA_PART_LINKS_TO_ENTITY:
      return {
        name: 'Part links to sequence',
        lookupStep: {
          type: LookupStepType.AA_PART_LINKS_TO_ENTITY,
          entitySchema: undefined,
        },
        parentSchema,
        linkSchema: null,
      };
    default:
      assert(false, `Unexpected step type ${stepType} for non schema field step`);
      return null as never;
  }
}

export function getLookupStepInfoForSourceDestinationStepItem(
  stepType: LookupStepType,
  sourceOrDestination: Array<LookupStepInfo> | null | undefined,
  parentSchema: Schema | null | undefined,
  hasPlateLayout: boolean
): LookupStepInfo {
  assert(
    [LookupStepType.SOURCE, LookupStepType.DESTINATION].includes(stepType),
    `Unexpected step type ${stepType} for source/destination step`
  );
  const baseName = stepType === LookupStepType.SOURCE ? 'Source' : 'Destination';
  const lookupStepContext = sourceOrDestination ?? [];

  const contextDescription = lookupStepContext
    .map((lookupStepInfo) => getStepDisplayInfo(lookupStepInfo).name)
    .join(` ${RIGHT_ARROW} `);

  const lookupStepInfo: LookupStepInfo & {lookupStepContext: Array<LookupStepInfo>} = {
    name: sourceOrDestination ? `${baseName} (${contextDescription})` : baseName,
    lookupStep: {type: stepType} as SourceLookupStepSpec | DestinationLookupStepSpec,
    lookupStepContext,
    parentSchema: parentSchema ?? null,
    linkSchema: null,
    hasPlateLayout,
  };
  const lastLookup = getStepItemWithSourceDestinationType(lookupStepInfo);
  if (lastLookup && lastLookup.schemaField != null) {
    lookupStepInfo.schemaField = lastLookup.schemaField;
  }
  return lookupStepInfo;
}

export function getLookupStepInfoForFieldStepItem(
  stepType: LookupStepType,
  field: SchemaField | null | undefined,
  parentSchema: Schema | null | undefined,
  typecastSchema?: Schema | null
): LookupStepInfo {
  const fieldDisplayName = field ? field.displayName : 'Deleted field';
  const linkSchema = field ? getRequiredLinkSchema(field) : null;
  switch (stepType) {
    case LookupStepType.SCHEMA_FIELD:
      return {
        name: fieldDisplayName,
        lookupStep: {
          type: LookupStepType.SCHEMA_FIELD,
          schemaField: field ? field.apiId : '',
          typecastSchema: typecastSchema?.id ?? null,
        },
        schemaField: field,
        parentSchema: parentSchema ?? null,
        linkSchema: typecastSchema ?? linkSchema ?? null,
        originalLinkSchema: linkSchema,
        typecastSchema: typecastSchema ?? null,
      };
    case LookupStepType.FILTER: {
      const filterType = field ? filterOptionsForFieldType(field.fieldType)[0] : FilterType.EQ;
      const shouldIncludeValue = filterType !== FilterType.ISNULL && filterType !== FilterType.NOTNULL;

      return {
        name: `Filter: ${fieldDisplayName}`,
        lookupStep: {
          type: LookupStepType.FILTER,
          schemaField: field ? field.apiId : '',
          filterType,
          ...(shouldIncludeValue && {value: ''}),
        },
        schemaField: field,
        parentSchema: parentSchema ?? null,
        linkSchema: null,
      };
    }
    default:
      assert(false, 'Unexpected step type for schema field step');
      return null as never;
  }
}

/**
 * Build the UI info for a "stored within" inventory filter (FILTER_BY_STORED_WITHIN).
 *
 * Unlike schema-field filters, this filter is not tied to a schema field; it filters the preceding
 * storable lookup down to items stored (directly or transitively) within the selected location.
 */
export function getStoredWithinFilterLookupStepInfo(
  parentSchema: Schema | null | undefined,
  value: string = ''
): StoredWithinFilterLookupStepInfo {
  return {
    name: STORED_WITHIN_FILTER_STEP_NAME,
    lookupStep: {
      type: LookupStepType.FILTER_BY_STORED_WITHIN,
      value,
    },
    parentSchema: parentSchema ?? null,
    linkSchema: null,
  };
}

function expandContainerByGridPositionLookupStep(
  parentSchema: Schema | null | undefined,
  lookupStep: ContainerByGridPositionLookupStepSpec
): Array<LookupStepInfo> {
  const gridField = parentSchema
    ? parentSchema.allFields.find(
        (field) => field.apiId === lookupStep.schemaField || field.systemName === lookupStep.schemaField
      )
    : null;
  const gridFieldLookupStepInfo = getLookupStepInfoForFieldStepItem(
    LookupStepType.SCHEMA_FIELD,
    gridField,
    parentSchema
  );
  const positionField = parentSchema
    ? parentSchema.allFields.find((field) => field.apiId === lookupStep.gridPositionSchemaField)
    : null;
  const positionFieldLookupStepInfo = getLookupStepInfoForFieldStepItem(
    LookupStepType.SCHEMA_FIELD,
    positionField,
    parentSchema
  );
  return [gridFieldLookupStepInfo, positionFieldLookupStepInfo];
}

function lookupStepToInfo(
  lookupStep: LookupStepSpec,
  parentSchema: Schema | null | undefined,
  context: LookupConfigModalContextInfo,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean,
  previousLookupStepType?: LookupStepType
): LookupStepInfo {
  let lookupStepInfo: LookupStepInfo;
  const {sourceLookupInfos, destinationLookupInfos, processTaskSchema, flowchartTaskSchema} = context;
  const usePlateLayout = context.usePlateLayout ?? false;
  if (isFieldStep(lookupStep)) {
    const schemaField = parentSchema
      ? parentSchema.allFields.find(
          (field) => field.apiId === lookupStep.schemaField || field.systemName === lookupStep.schemaField
        )
      : null;
    let typecastSchema: Schema | null = null;
    if (isStepWithTypecastSchemaMetadata(lookupStep) && lookupStep.typecastSchema) {
      typecastSchema = getTranslatedTagSchemaFromCache(lookupStep.typecastSchema);
      assert(typecastSchema != null, 'Expected typecast schema to be defined');
    }
    lookupStepInfo = getLookupStepInfoForFieldStepItem(
      lookupStep.type,
      schemaField,
      parentSchema,
      typecastSchema
    );
  } else if (lookupStep.type === LookupStepType.SCHEMA_IDENTITY) {
    lookupStepInfo = getLookupStepInfoForSchemaIdentityStepItem(
      getTagSchemaOrSchemaInterfaceFromCache(lookupStep.entitySchema),
      parentSchema
    );
  } else if (lookupStep.type === LookupStepType.COUNT) {
    lookupStepInfo = getLookupStepInfoForCountStepItem(parentSchema, schemaFieldFilterFn);
  } else if (lookupStep.type === LookupStepType.SOURCE) {
    lookupStepInfo = getLookupStepInfoForSourceDestinationStepItem(
      lookupStep.type,
      sourceLookupInfos,
      parentSchema,
      usePlateLayout
    );
  } else if (lookupStep.type === LookupStepType.DESTINATION) {
    lookupStepInfo = getLookupStepInfoForSourceDestinationStepItem(
      lookupStep.type,
      destinationLookupInfos,
      parentSchema,
      usePlateLayout
    );
  } else if (lookupStep.type === LookupStepType.FLOWCHART_PROCESS_TASK) {
    assert(flowchartTaskSchema != null, 'Expected flowchart schema to be defined');
    lookupStepInfo = getLookupStepInfoForFlowchartProcessTaskStepItem(flowchartTaskSchema, parentSchema);
  } else if (lookupStep.type === LookupStepType.TASK_FINDER_PROCESS_TASKS) {
    lookupStepInfo = getLookupStepInfoForProcessTaskStepItem(processTaskSchema, parentSchema);
  } else if (lookupStep.type === LookupStepType.CONTAINER_BY_GRID_POSITION) {
    lookupStepInfo = getLookupStepInfoForContainerByGridPositionStepItem(lookupStep, parentSchema);
  } else if (lookupStep.type === LookupStepType.FILTER_BY_STORED_WITHIN) {
    lookupStepInfo = getStoredWithinFilterLookupStepInfo(parentSchema, lookupStep.value);
  } else if (lookupStep.type === LookupStepType.NOTEBOOK_COLUMN_TYPECHECK) {
    lookupStepInfo = getLookupStepInfoForNotebookTypeCheckItem(lookupStep, parentSchema, context);
  } else {
    lookupStepInfo = getLookupStepInfoForNonFieldStepItem(
      lookupStep.type,
      parentSchema ?? null,
      previousLookupStepType
    );
  }
  return {
    ...lookupStepInfo,
    lookupStep,
    linkSchema: getLinkSchemaForLookupStep(lookupStep, parentSchema ?? null),
  };
}

function getSchemaForFollowingStep(
  lookupStepInfo: LookupStepInfo,
  currSchema: Schema | null | undefined
): Schema | null | undefined {
  let schema = currSchema;
  if (hasLookupStepContext(lookupStepInfo)) {
    const itemWithType = getStepItemWithSourceDestinationType(lookupStepInfo);
    if (itemWithType) {
      const lookupStepInfoWithType = getLookupStepInfoWithType(itemWithType);
      schema = lookupStepInfoWithType?.linkSchema;
    }
  } else if (!MODIFIER_LOOKUP_STEP_TYPES.includes(lookupStepInfo.lookupStep.type)) {
    const lookupStepInfoWithType = getLookupStepInfoWithType(lookupStepInfo);
    schema = lookupStepInfoWithType?.linkSchema;
  }
  return schema;
}

function attachFilterStep(lookupStepInfo: LookupStepInfo, filterLookupStepInfo: FilterLookupStepInfo): void {
  if (lookupStepInfo.filterLookupSteps) {
    lookupStepInfo.filterLookupSteps.push(filterLookupStepInfo);
  } else {
    lookupStepInfo.filterLookupSteps = [filterLookupStepInfo];
  }
}

function attachModifierStep(
  lookupStepInfo: LookupStepInfo,
  modifierLookupStepInfo: ModifierLookupStepInfo
): void {
  if (lookupStepInfo.modifierLookupSteps) {
    lookupStepInfo.modifierLookupSteps.push(modifierLookupStepInfo);
  } else {
    lookupStepInfo.modifierLookupSteps = [modifierLookupStepInfo];
  }
}

function attachCollapsingStep(
  lookupStepInfo: LookupStepInfo,
  collapsingLookupStepInfo: CollapsingLookupStepInfo
): void {
  if (lookupStepInfo.collapsingLookupSteps) {
    lookupStepInfo.collapsingLookupSteps.push(collapsingLookupStepInfo);
  } else {
    lookupStepInfo.collapsingLookupSteps = [collapsingLookupStepInfo];
  }
}

class InvalidLookupStepForModalError extends ErrorSubclass {}

export function lookupStepsToInfos(
  lookupSteps: Array<LookupStepSpec>,
  parentSchema: Schema | null | undefined,
  context: LookupConfigModalContextInfo,
  schemaFieldTreeFilterFn: ((field: SchemaField) => boolean) | undefined,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean,
  allowMissingParentSchema?: boolean,
  isDetailStepRequired?: boolean,
  injectedFinalLookupSteps?: Array<InjectedLookupStep>
): {
  baseLookupStepInfos: Array<LookupStepInfo>;
  detailLookupStepInfo: LookupStepInfo | null;
  replicatesLookupStepInfo: (LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec}) | null;
  processTasksLookupStepInfo: (LookupStepInfo & {lookupStep: TaskFinderProcessTasksLookupStepSpec}) | null;
} {
  /**
   * Requires dependency schemas to be loaded in the cache.
   */
  // If parent schema is missing, we want to return the default blank data even if there are lookup steps.
  if (parentSchema == null && !allowMissingParentSchema) {
    return {
      baseLookupStepInfos: [],
      detailLookupStepInfo: null,
      replicatesLookupStepInfo: null,
      processTasksLookupStepInfo: null,
    };
  }
  const baseLookupStepInfos: Array<LookupStepInfo> = [];
  let detailLookupStepInfo: LookupStepInfo | null = null;
  let replicatesLookupStepInfo: (LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec}) | null = null;
  let processTasksLookupStepInfo:
    | (LookupStepInfo & {lookupStep: TaskFinderProcessTasksLookupStepSpec})
    | null = null;
  let schema: Schema | null | undefined = parentSchema;
  for (let i = 0; i < lookupSteps.length; i++) {
    const lookupStep = lookupSteps[i];
    const lookupStepInfo = lookupStepToInfo(
      lookupStep,
      schema,
      context,
      schemaFieldFilterFn,
      i > 0 ? lookupSteps[i - 1].type : undefined
    );
    schema = getSchemaForFollowingStep(lookupStepInfo, schema);
    const isLastStep = i === lookupSteps.length - 1;
    const followingSteps = lookupSteps.slice(i + 1, lookupSteps.length);
    if (lookupStepInfo.lookupStep.type === LookupStepType.REPLICATES) {
      replicatesLookupStepInfo = lookupStepInfo as LookupStepInfo & {lookupStep: ReplicatesLookupStepSpec};
      if (i !== lookupSteps.length - 1) {
        throw new InvalidLookupStepForModalError('Replicates is not the last step');
      }
    } else if (
      lookupStepInfo.lookupStep.type === LookupStepType.FILTER ||
      lookupStepInfo.lookupStep.type === LookupStepType.FILTER_BY_STORED_WITHIN
    ) {
      attachFilterStep(
        detailLookupStepInfo || baseLookupStepInfos[baseLookupStepInfos.length - 1],
        lookupStepInfo as FilterLookupStepInfo
      );
    } else if (lookupStepInfo.lookupStep.type === LookupStepType.FILTER_BY_ORIGIN) {
      attachModifierStep(
        detailLookupStepInfo || baseLookupStepInfos[baseLookupStepInfos.length - 1],
        lookupStepInfo as ModifierLookupStepInfo
      );
    } else if (lookupStepInfo.lookupStep.type === LookupStepType.LATEST) {
      attachCollapsingStep(
        detailLookupStepInfo || baseLookupStepInfos[baseLookupStepInfos.length - 1],
        lookupStepInfo as CollapsingLookupStepInfo
      );
    } else if (lookupStepInfo.lookupStep.type === LookupStepType.TASK_FINDER_PROCESS_TASKS) {
      processTasksLookupStepInfo = lookupStepInfo as LookupStepInfo & {
        lookupStep: TaskFinderProcessTasksLookupStepSpec;
      };
      if (i !== lookupSteps.length - 1) {
        throw new InvalidLookupStepForModalError('Task is not the last step');
      }
    } else if (
      isDetailStep(
        lookupStepInfo,
        schemaFieldTreeFilterFn,
        isLastStep ||
          // Steps that are only followed by filter steps can be considered as final steps.
          followingSteps.every(
            (lookupStep) =>
              lookupStep.type === LookupStepType.FILTER ||
              lookupStep.type === LookupStepType.FILTER_BY_STORED_WITHIN
          ) ||
          // Steps that are only followed by task finder step can be considered as final steps.
          (followingSteps.length === 1 &&
            followingSteps[0].type === LookupStepType.TASK_FINDER_PROCESS_TASKS),
        isDetailStepRequired
      ) &&
      i !== 0
    ) {
      const injectedStep = injectedFinalLookupSteps?.find((injection) =>
        isEqual(injection.lookupStep, lookupStep)
      );
      if (injectedStep) {
        detailLookupStepInfo = {
          name: injectedStep.name,
          lookupStep,
          shouldSkipValidation: true,
          parentSchema: null, // TODO is this right
          linkSchema: getLinkSchemaForLookupStep(lookupStep, null),
        };
      } else {
        detailLookupStepInfo = lookupStepInfo;
      }

      if (
        !followingSteps.every((lookupStep) =>
          [
            LookupStepType.FILTER,
            LookupStepType.FILTER_BY_STORED_WITHIN,
            LookupStepType.REPLICATES,
            LookupStepType.TASK_FINDER_PROCESS_TASKS,
          ].includes(lookupStep.type)
        )
      ) {
        throw new InvalidLookupStepForModalError(
          'Steps other than FILTER, FILTER_BY_STORED_WITHIN, REPLICATES or PROCESS_TASKS after detail step'
        );
      }
    } else {
      baseLookupStepInfos.push(lookupStepInfo);
    }
  }
  return {baseLookupStepInfos, detailLookupStepInfo, replicatesLookupStepInfo, processTasksLookupStepInfo};
}

export function lookupStepsToInfosArray(
  lookupSteps: Array<LookupStepSpec>,
  parentSchema: Schema | null | undefined,
  context: LookupConfigModalContextInfo,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean
): Array<LookupStepInfo> {
  if (parentSchema == null) {
    return [];
  }
  const lookupStepInfos: Array<LookupStepInfo> = [];
  let schema: Schema | null | undefined = parentSchema;
  for (let i = 0; i < lookupSteps.length; i++) {
    const lookupStep = lookupSteps[i];
    const lookupStepInfo = lookupStepToInfo(
      lookupStep,
      schema,
      context,
      schemaFieldFilterFn,
      i > 0 ? lookupSteps[i - 1].type : undefined
    );
    schema = getSchemaForFollowingStep(lookupStepInfo, schema);
    if (
      lookupStepInfo.lookupStep.type === LookupStepType.FILTER ||
      lookupStepInfo.lookupStep.type === LookupStepType.FILTER_BY_STORED_WITHIN
    ) {
      attachFilterStep(lookupStepInfos[lookupStepInfos.length - 1], lookupStepInfo as FilterLookupStepInfo);
    } else if (lookupStepInfo.lookupStep.type === LookupStepType.CONTAINER_BY_GRID_POSITION) {
      const extraLookupSteps = expandContainerByGridPositionLookupStep(
        parentSchema,
        lookupStep as ContainerByGridPositionLookupStepSpec
      );
      lookupStepInfos.push(...extraLookupSteps);
      lookupStepInfos.push(lookupStepInfo);
    } else {
      lookupStepInfos.push(lookupStepInfo);
    }
  }
  return lookupStepInfos;
}

export const MAX_LOOKUP_STEP_FORMS = 5;

export function lookupStepInfosToGroups(
  lookupStepInfos: Array<LookupStepInfo>
): Array<Array<LookupStepInfo>> {
  const lookupStepGroups: Array<Array<LookupStepInfo>> = [...Array(MAX_LOOKUP_STEP_FORMS).keys()].map(
    () => []
  );
  if (!lookupStepInfos.length) {
    return lookupStepGroups;
  }
  lookupStepGroups[0].push(lookupStepInfos[0]);
  let currLookupStepGroupIndex = 0;
  for (let i = 1; i < lookupStepInfos.length; i++) {
    const lookupStepInfo = lookupStepInfos[i];
    if (!isFieldStep(lookupStepInfo.lookupStep)) {
      currLookupStepGroupIndex += 1;
    }
    lookupStepGroups[currLookupStepGroupIndex].push(lookupStepInfo);
  }
  return lookupStepGroups;
}

export function isLookupConfigValidForModal<TExtraState>(
  modalProps: MakeFieldsOptional<LookupConfigModalProps<TExtraState>, 'onRequestSubmit' | 'onRequestClose'>
): boolean {
  try {
    if (
      modalProps.initialModalPane === LookupConfigModalPane.LookupDetail &&
      !modalProps.initialLookupConfig
    ) {
      throw new InvalidLookupStepForModalError(
        'Tried to open the lookup config modal directly to the detail pane but no initial lookup config was provided'
      );
    }
    const {baseLookupStepInfos} = lookupStepsToInfos(
      modalProps.initialLookupConfig ? modalProps.initialLookupConfig.lookupSteps : [],
      modalProps.baseSchema,
      modalProps.context ?? {},
      modalProps.schemaFieldTreeFilterFn,
      modalProps.schemaFieldFilterFn,
      modalProps.allowMissingParentSchema
    );
    const lookupGroups = lookupStepInfosToGroups(baseLookupStepInfos);
    for (const lookupGroup of lookupGroups) {
      if (
        !lookupGroup.slice(0, lookupGroup.length - 1).every((lookupGroup) => !lookupGroup.filterLookupSteps)
      ) {
        throw new InvalidLookupStepForModalError(
          'FILTER steps in places other than the last step of a lookup group'
        );
      }
      // ensure that modifier steps are only being used on the first step of the group
      if (
        !lookupGroup.slice(1, lookupGroup.length).every((lookupGroup) => !lookupGroup.modifierLookupSteps)
      ) {
        throw new InvalidLookupStepForModalError(
          'Modifier steps are currently only supported on the first step of a lookup group'
        );
      }
    }
  } catch (e) {
    if (e instanceof InvalidLookupStepForModalError) {
      logging.warn(`Unable to edit lookup config via UI: ${e.message}`);
      return false;
    } else {
      throw e;
    }
  }
  return true;
}
